import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  WORKFLOW_ACTIONS,
} from '../../../components/Admin/Workflows/Wizard/WorkflowContext';
import {
  NODE_CATEGORIES,
  NODE_TYPES,
  WORKFLOW_DEFAULTS,
  APPROVAL_TYPE_CODES,
} from '../../../constants/workflow.constant';
import Typography from '../../../components/common/StyledTypography';

const useNodeEventHandlers = ({ approvalTypes }) => {
  const { t } = useTranslation();
  const { nodes, edges, setNodes, setEdges, openStageFormModal, dispatch } =
    useWorkflow();
  const clickTimeoutRef = useRef(null);
  const deletedNodeIds = useRef(new Set());

  const applyFormOnNode = useCallback(
    (nodeStageId, jsonContent) => {
      if (openStageFormModal.open) {
        dispatch({ type: WORKFLOW_ACTIONS.CLOSE_STAGE_FORM_MODAL });
      }

      const currentEdges = edges;
      const childNodeIds = currentEdges
        .filter((edge) => edge.source === nodeStageId)
        .map((edge) => edge.target);

      const mainNode = nodes.find((n) => n.id === nodeStageId);

      const hasReviewChildNode = currentEdges.some((edge) => {
        const targetNode = nodes.find((n) => n.id === edge.target);
        return (
          edge.source === nodeStageId && targetNode?.scope === 'ReviewChildNode'
        );
      });

      if (jsonContent.approver_role_id && !hasReviewChildNode) {
        const parentPosition = mainNode.position || { x: 150, y: 150 };
        const newNodes = [];
        const newEdges = [];
        let childOffsetX = -383;

        approvalTypes.forEach((child) => {
          if (child.code === APPROVAL_TYPE_CODES.SEND_TO_ASSESSMENT) return;
          if (
            mainNode.category === NODE_CATEGORIES.ASSESSMENT_REVIEW &&
            child.code === APPROVAL_TYPE_CODES.SEND_BACK
          )
            return;

          if (!childNodeIds.includes(child.code)) {
            const childNodeId = `Approval-${nodeStageId}-${child.id}-${child.code}-${Math.floor(Math.random() * 10000)}`;

            newNodes.push({
              id: childNodeId,
              type: `approval-${child.id}`,
              scope: 'ReviewChildNode',
              category: `child-${child.code}`,
              data: {
                label: (
                  <Typography component="h6" variant="subtitle">
                    {child.name}
                  </Typography>
                ),
                approvalTypeId: child.id,
                name: child.name,
                formConfig: {},
              },
              className: 'child-node',
              position: {
                x: parentPosition.x + (childOffsetX += 200),
                y: parentPosition.y + 90,
              },
            });

            newEdges.push({
              id: `edge-${nodeStageId}-${childNodeId}-${Math.floor(Math.random() * 10000)}`,
              source: nodeStageId,
              target: childNodeId,
              type: WORKFLOW_DEFAULTS.EDGE_TYPE,
              style: WORKFLOW_DEFAULTS.EDGE_STYLE,
            });
          }
        });

        setNodes((prevNodes) => [...prevNodes, ...newNodes]);
        setEdges((prevEdges) => [...prevEdges, ...newEdges]);
      } else if (jsonContent.target_category_id) {
        const existingReviewConnection = currentEdges.some((edge) => {
          if (edge.source !== nodeStageId) return false;
          const targetNode = nodes.find((n) => n.id === edge.target);
          return (
            targetNode?.category === NODE_CATEGORIES.REVIEW ||
            targetNode?.category === NODE_CATEGORIES.STAGE
          );
        });

        const isNewSubmission =
          mainNode.category !== NODE_CATEGORIES.SUBMISSION;

        if (!existingReviewConnection && isNewSubmission) {
          const parentPosition = mainNode.position || { x: 150, y: 150 };
          const reviewNodeId = `FormStage-review-${nodeStageId}-${Math.floor(Math.random() * 10000)}`;

          const autoReviewNode = {
            id: reviewNodeId,
            type: NODE_TYPES.FORM_STAGE,
            position: {
              x: parentPosition.x,
              y: parentPosition.y + 120,
            },
            category: NODE_CATEGORIES.STAGE,
            data: {
              label: (
                <Typography component="h6" variant="subtitle">
                  {t('workflowBuilder.reviewStage')}
                </Typography>
              ),
              name: t('workflowBuilder.reviewStage'),
              id: null,
              formConfig: {
                name: `${t('workflowBuilder.reviewPrefix')}${jsonContent.name || t('workflowBuilder.form')}`,
                form_id: jsonContent.form_id,
                form_ids: [jsonContent.form_id],
                stage_type_id: 3,
              },
            },
          };

          setNodes((prevNodes) => [...prevNodes, autoReviewNode]);

          const autoReviewEdge = {
            id: `edge-${nodeStageId}-${reviewNodeId}-${Math.floor(Math.random() * 10000)}`,
            source: nodeStageId,
            target: reviewNodeId,
            type: WORKFLOW_DEFAULTS.EDGE_TYPE,
            style: WORKFLOW_DEFAULTS.EDGE_STYLE,
          };
          setEdges((prevEdges) => [...prevEdges, autoReviewEdge]);

          setTimeout(() => {
            dispatch({
              type: WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL,
              payload: {
                open: true,
                nodeStageId: reviewNodeId,
                content: autoReviewNode.data.formConfig,
              },
            });
          }, 100);
        }
      }

      const updatedNode = {
        ...mainNode,
        category: jsonContent.target_category_id
          ? NODE_CATEGORIES.SUBMISSION
          : mainNode.category === NODE_CATEGORIES.ASSESSMENT_REVIEW
            ? NODE_CATEGORIES.ASSESSMENT_REVIEW
            : NODE_CATEGORIES.REVIEW,
        data: {
          ...mainNode.data,
          label: (
            <Typography component="h6" variant="subtitle">
              {jsonContent.name || t('workflowBuilder.unnamedStage')}
            </Typography>
          ),
          name: jsonContent.name || t('workflowBuilder.unnamedStage'),
          formConfig: jsonContent,
        },
      };

      setNodes((prevNodes) =>
        prevNodes.map((node) =>
          node.id === updatedNode.id ? updatedNode : node
        )
      );

      return updatedNode;
    },
    [
      nodes,
      edges,
      openStageFormModal,
      setNodes,
      setEdges,
      dispatch,
      approvalTypes,
      t,
    ]
  );

  const handleCloseFormModal = useCallback(
    (nodeStageId) => {
      const node = nodes.find((n) => n.id === nodeStageId);
      const formConfig = node?.data?.formConfig;
      if (node?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW) return;

      const hasValidForm =
        formConfig?.stage_type_id === 3
          ? formConfig?.form_ids && formConfig.form_ids.length > 0
          : !!formConfig?.form_id;

      if (!formConfig?.name || !hasValidForm) {
        setNodes((nds) => nds.filter((n) => n.id !== nodeStageId));
        setEdges((eds) =>
          eds.filter(
            (e) => e.source !== nodeStageId && e.target !== nodeStageId
          )
        );
      }
    },
    [nodes, setNodes, setEdges]
  );

  const onNodeClick = useCallback(
    (event, node) => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      event.stopPropagation();

      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        if (deletedNodeIds.current.has(node.id)) return;
        if (node.type !== NODE_TYPES.FORM_STAGE) return;

        dispatch({
          type: WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL,
          payload: {
            open: true,
            nodeStageId: node.id,
            content: node.data?.formConfig || null,
          },
        });
      }, 500);
    },
    [dispatch]
  );

  const onNodeDoubleClick = useCallback(
    (event, node) => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      event.stopPropagation();

      if (node.type === NODE_TYPES.INPUT) return;

      deletedNodeIds.current.add(node.id);

      if (node.data?.formConfig?.approver_role_id) {
        const childNodeIds = edges
          .filter((edge) => edge.source === node.id)
          .map((edge) => {
            const targetNode = nodes.find((n) => n.id === edge.target);
            return targetNode?.scope === 'ReviewChildNode' ? edge.target : null;
          })
          .filter(Boolean);

        setNodes((nds) =>
          nds.filter((n) => n.id !== node.id && !childNodeIds.includes(n.id))
        );
        setEdges((eds) =>
          eds.filter(
            (e) =>
              e.source !== node.id &&
              e.target !== node.id &&
              !childNodeIds.includes(e.source) &&
              !childNodeIds.includes(e.target)
          )
        );
      } else if (node?.category === NODE_CATEGORIES.CHILD_STA) {
        const childNodeIds = new Set(
          edges
            .filter((edge) => edge.source === node.id)
            .map((edge) => edge.target)
        );
        const grandChildNodeIds = edges
          .filter((edge) => childNodeIds.has(edge.source))
          .map((edge) => edge.target);
        const allDescendantIds = new Set([
          ...childNodeIds,
          ...grandChildNodeIds,
        ]);

        setNodes((nds) =>
          nds.filter((n) => n.id !== node.id && !allDescendantIds.has(n.id))
        );
        setEdges((eds) =>
          eds.filter(
            (e) =>
              e.source !== node.id &&
              e.target !== node.id &&
              !allDescendantIds.has(e.source) &&
              !allDescendantIds.has(e.target)
          )
        );
      } else {
        setNodes((nds) => nds.filter((n) => n.id !== node.id));
        setEdges((eds) =>
          eds.filter((e) => e.source !== node.id && e.target !== node.id)
        );
      }
    },
    [nodes, edges, setNodes, setEdges]
  );

  const onEdgeClick = useCallback(
    (event, edge) => {
      event.stopPropagation();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges]
  );

  const clearDeletedNodeIds = useCallback(() => {
    deletedNodeIds.current.clear();
  }, []);

  return {
    applyFormOnNode,
    handleCloseFormModal,
    onNodeClick,
    onNodeDoubleClick,
    onEdgeClick,
    clearDeletedNodeIds,
  };
};

export default useNodeEventHandlers;
